import type * as THREE from 'three';
import type CameraControls from 'camera-controls';
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { RoleDef, HdrPreset } from '../materials/roleDefs.js';
import type { GlbSource } from '../ui/materialCategoryRules.js';
import type { createCameraLimits } from '../scene/cameraLimits.js';
import type { ViewerConfig } from './config.js';

/** 도구 간 통신 이벤트. 새 이벤트는 여기 추가. */
export interface EventMap {
  'paint:change': void;     // paint 색 변경 → tuner 활성 카테고리 슬라이더 갱신
  'paint:select': string;   // hex — tuner Studio 드롭다운 → paint swatch 선택
}

export interface ViewerEvents {
  on<K extends keyof EventMap>(k: K, fn: (p: EventMap[K]) => void): void;
  emit<K extends keyof EventMap>(k: K, ...args: EventMap[K] extends void ? [] : [EventMap[K]]): void;
}

export function createEventHub(): ViewerEvents {
  const map = new Map<keyof EventMap, Array<(p: never) => void>>();
  return {
    on(k, fn) {
      const a = map.get(k) ?? [];
      a.push(fn as (p: never) => void);
      map.set(k, a);
    },
    emit(k, ...args) {
      for (const fn of map.get(k) ?? []) (fn as (p: unknown) => void)(args[0]);
    },
  };
}

/** 코어가 도구에 넘기는 공유 핸들. 도구는 셸을 몰라도 이것만 받으면 동작. */
export interface ViewerContext {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: CameraControls;
  cameraLimits: ReturnType<typeof createCameraLimits>;
  invalidate(): void;
  gltfLoader: GLTFLoader;
  roots: Map<GlbSource, THREE.Object3D>;   // 로드된 차 (RAV4: { O } (+ finalizeFirstPaint 후 X))
  root: THREE.Object3D;                    // primary (roots 'O')
  roleDefs: Record<string, RoleDef>;
  rolesOn: boolean;
  hemiLight: THREE.HemisphereLight;
  hdriMap: Record<string, string>;
  env: {
    getEnvMap(): THREE.Texture | null;
    setHdr(keyOrPath: string, forceType?: 'hdr' | 'exr'): Promise<void>;
    hdriKeys: string[];
    currentPreset: string;
  };
  config: ViewerConfig;
  /** 로드된 primary GLB 가 baked/deploy 산출물(파일에 `+++` 재질 = aggressive)인지.
   *  런타임 deriveFor 가 `+++` 를 만들기 전(로드 직후) 판정. tuner 가 overrides[] 저작 차단에 사용. */
  bakedGlb: boolean;
  params: URLSearchParams;
  shadowTexPromise: Promise<THREE.Texture>;
  events: ViewerEvents;
  /** role-pbr JSON 에서 로드한 사용자 튜닝 (paint/pairing 도구가 사용) */
  saved: {
    hdr: HdrPreset | null;
    paintOverrides: Record<string, Record<string, Record<string, unknown>>>;
    currentPaint: string | null;
  };
  /** paint 도구가 만든 linkState. tuner 가 Studio 폴더에서 공유. paint 비활성 시 undefined. */
  paintLinkState?: import('../materials/paintOverrides.js').PaintLinkState;
  /** paint 도구가 만든 스와치 패널(#paint-sidebar). accessoryMenu 가 우측 레일 컬러 패널로 이관. */
  paintPanel?: { el: HTMLElement; controller: import('../ui/paintSwatches.js').PaintSwatchesController };
}
